import { getNumber } from "../menuEngineeringUtils";
import { getFoodCost, getItemMix, hasCost, RANK_ACTIONS } from "./menuDashboardInsights";

const round = (value, digits = 2) => Number(getNumber(value).toFixed(digits));
const formatDate = (seconds) => (seconds ? new Date(seconds * 1000).toISOString().slice(0, 10) : "");

export const getDishRows = (items, menuQuantity) =>
  items.map((item, index) => ({
    Rank: index + 1,
    Dish: item.itemDescription || String(item.itemRef ?? ""),
    "Item ref": item.itemRef ?? "",
    Page: item.pageName || "",
    Category: item.categoryName || "",
    Class: item._rank,
    Action: RANK_ACTIONS[item._rank],
    Sold: round(item.quantity, 0),
    "Share %": round(getItemMix(item, menuQuantity)),
    "Avg price": round(item.avgPrice),
    "Avg cost": hasCost(item) ? round(item.avgCost) : null,
    "Food cost %": getFoodCost(item) == null ? null : round(getFoodCost(item) * 100, 1),
    "Margin per item": round(item.avgProfit),
    "Total contribution": round(item.totalProfit),
    "Total sales": round(item.totalSales),
  }));

export const getExportRows = (items, menuQuantity) => {
  const dishRows = getDishRows(items, menuQuantity);
  const sum = (list, key) => list.reduce((total, item) => total + getNumber(item[key]), 0);
  const quantity = sum(items, "quantity");
  const profit = sum(items, "totalProfit");
  const costedItems = items.filter(hasCost);
  const costedQuantity = sum(costedItems, "quantity");
  const costedSales = sum(costedItems, "totalSales");
  const costedCost = sum(costedItems, "totalCost");
  const totalSales = sum(items, "totalSales");

  return [
    ...dishRows,
    {
      Rank: "Total",
      Dish: `${items.length} dishes`,
      "Item ref": "",
      Page: "",
      Category: "",
      Class: "",
      Action: "",
      Sold: round(quantity, 0),
      "Share %": round(items.reduce((total, item) => total + getItemMix(item, menuQuantity), 0)),
      "Avg price": round(quantity ? totalSales / quantity : 0),
      "Avg cost": costedItems.length && costedQuantity ? round(costedCost / costedQuantity) : null,
      "Food cost %": costedSales ? round((costedCost / costedSales) * 100, 1) : null,
      "Margin per item": round(quantity ? profit / quantity : 0),
      "Total contribution": round(profit),
      "Total sales": round(totalSales),
    },
  ];
};

const getSummaryRows = ({ meta, totals, popularityLine, marginLine, coverage }) => [
  ["Outlet", meta.outlet],
  ["View", meta.view],
  ["Period", `${formatDate(meta.period?.start)} to ${formatDate(meta.period?.end)} (${meta.days} day${meta.days === 1 ? "" : "s"})`],
  ["Currency", "QAR"],
  [],
  ["Revenue", round(totals.revenue)],
  ["Contribution margin", round(totals.profit)],
  ["Food cost %", round(totals.foodCost * 100, 1)],
  ["Items sold", round(totals.quantity, 0)],
  ["Dishes with a cost", coverage.ratedItems],
  ["Dishes without a cost", coverage.totalItems - coverage.ratedItems],
  [],
  ["Popularity line %", round(popularityLine)],
  ["Margin line", round(marginLine)],
  [],
  ["Method", "Classifications and decision lines come from the outlet's menu-engineering results. Modifiers (item ref above 3600) are excluded unless switched on."],
];

const getFileName = (meta, extension) => {
  const safe = (value) => String(value || "all").replace(/[^\w\u0600-\u06FF-]+/g, "_");
  return `menu-engineering_${safe(meta.outlet)}_${safe(meta.view)}_${formatDate(meta.period?.start)}_${formatDate(meta.period?.end)}.${extension}`;
};

export async function exportExcel(report) {
  const XLSX = await import("xlsx");
  const workbook = XLSX.utils.book_new();
  const dishes = XLSX.utils.json_to_sheet(getExportRows(report.items, report.menuQuantity));
  XLSX.utils.book_append_sheet(workbook, dishes, "Dishes");
  const summary = XLSX.utils.aoa_to_sheet(getSummaryRows(report));
  summary["!cols"] = [{ wch: 26 }, { wch: 90 }];
  XLSX.utils.book_append_sheet(workbook, summary, "Summary");
  XLSX.writeFile(workbook, getFileName(report.meta, "xlsx"));
}

export async function exportCsv(report) {
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.json_to_sheet(getExportRows(report.items, report.menuQuantity));
  // The BOM lets Excel open Arabic dish names correctly.
  const blob = new Blob([`\uFEFF${XLSX.utils.sheet_to_csv(sheet)}`], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = getFileName(report.meta, "csv");
  link.click();
  URL.revokeObjectURL(link.href);
}

export async function exportPdf(report) {
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(16);
  doc.text(`Menu engineering · ${report.meta.outlet}`, 40, 40);
  autoTable(doc, {
    startY: 56,
    theme: "plain",
    styles: { fontSize: 9, cellPadding: 2 },
    body: getSummaryRows(report).filter((row) => row.length && row[0] !== "Method"),
  });
  const rows = getExportRows(report.items, report.menuQuantity);
  const columns = ["Rank", "Dish", "Class", "Action", "Sold", "Share %", "Avg price", "Avg cost", "Food cost %", "Margin per item", "Total contribution"];
  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [columns],
    body: rows.map((row) => columns.map((column) => row[column] ?? "—")),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [43, 37, 32] },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === rows.length - 1) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [235, 235, 235];
      }
    },
  });
  doc.save(getFileName(report.meta, "pdf"));
}
